import React, { useCallback, useMemo } from 'react';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import useEntityDefinitions from './useEntityDefinitions';
import useEntityDetailsApps from './useEntityDetailsApps';

const EntityDefinitionsProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { entityDefinitions, isLoading: isLoadingEntityDefinitions } =
    useEntityDefinitions();
  const {
    entityDetailsApps,
    isLoading: isLoadingEntityDetailsApps,
    localUpdateOrAddApp,
  } = useEntityDetailsApps();

  const defaultAppLookup = useMemo((): {
    [field: string]:
      | {
          entity: EntityDetails.Entity;
          app: EntityDetails.App;
        }
      | undefined;
  } => {
    const lookup: {
      [field: string]:
        | {
            entity: EntityDetails.Entity;
            app: EntityDetails.App;
          }
        | undefined;
    } = {};

    entityDefinitions.forEach((entity) => {
      const defaultApp =
        entityDetailsApps.find((app) => app.id === entity.defaultAppId) ??
        entityDetailsApps.find((app) => app.entityId === entity.id);

      if (defaultApp) {
        lookup[entity.primaryField] = {
          entity,
          app: defaultApp,
        };
      }
    });

    return lookup;
  }, [entityDefinitions, entityDetailsApps]);

  const appSlugLookup = useMemo((): {
    [slug: string]:
      | {
          entity: EntityDetails.Entity;
          app: EntityDetails.App;
        }
      | undefined;
  } => {
    const lookup: {
      [slug: string]:
        | {
            entity: EntityDetails.Entity;
            app: EntityDetails.App;
          }
        | undefined;
    } = {};

    entityDetailsApps.forEach((app) => {
      const entity = entityDefinitions.find((def) => def.id === app.entityId);
      if (!!entity) {
        lookup[app.urlSlug] = {
          app,
          entity,
        };
      }
    });

    return lookup;
  }, [entityDefinitions, entityDetailsApps]);

  const getEntityAndAppForSlug = useCallback(
    ({ slug }: { slug: string }) => {
      return appSlugLookup[slug];
    },
    [appSlugLookup],
  );

  const getEntityAndDefaultAppForField = useCallback(
    ({ field }: { field: string }) => {
      return defaultAppLookup[field];
    },
    [defaultAppLookup],
  );

  return (
    <EntityDefinitionsContext.Provider
      value={{
        entityDefinitions,
        entityDetailsApps,
        localUpdateOrAddApp,
        isLoading: isLoadingEntityDetailsApps || isLoadingEntityDefinitions,
        getEntityAndAppForSlug,
        getEntityAndDefaultAppForField,
      }}
    >
      {children}
    </EntityDefinitionsContext.Provider>
  );
};

export default EntityDefinitionsProvider;
